import {
    AddressDto,
    DeviceOwnerDto,
    DeviceOwnerDtoAccountStatusEnum,
    DeviceOwnerDtoTypeEnum,
    TaskInfoDtoStageEnum,
    TaskInfoUpdateMessage,
    TaskInfoUpdateMessageStageEnum,
    UserAccountSummary,
} from "@/api/openapi";
import { IStoredTask } from "@/contexts/tasks";

export interface IDrEventInfo {
    drEventId: string;
    dnsp: string;
    start: Date;
    end: Date;
    optIns: number;
    optOuts: number;
}

export interface IDrEventStatistics {
    drEventId: string;
    dnsp: string;
    kWhSaved: number;
    participants: number;
    chargers: number;
    start: Date;
    end: Date;
}

export function convertTaskInfoUpdateMessageToStoredTask(
    taskInfo: TaskInfoUpdateMessage,
): IStoredTask {
    return {
        deviceIdentity: taskInfo.deviceId!,
        taskInfoId: taskInfo.taskInfoId!,
        taskType: taskInfo.taskType!,
        stage: convertTaskStageDtoToTaskStageViewModel(taskInfo.stage),
        result: taskInfo.result,
    };
}

export enum TaskStageViewModel {
    Requested = "Requested",
    Started = "Started",
    Complete = "Complete",
    Failed = "Failed",
    Cancelled = "Cancelled",
    Unknown = "Unknown",
}

export function convertTaskStageDtoToTaskStageViewModel(
    stage: TaskInfoDtoStageEnum | TaskInfoUpdateMessageStageEnum | undefined,
): TaskStageViewModel {
    return (stage as TaskStageViewModel) || TaskStageViewModel.Unknown;
}

export interface IGraphChargerMeterValueItem {
    chargerMeterValueId: number;
    timestamp: string;
    totalActiveEnergyImportWh?: number;
    totalActiveEnergyExportWh?: number;
    l1VoltageRmsV?: number;
    l2VoltageRmsV?: number;
    l3VoltageRmsV?: number;
    l1ActivePowerRmsW?: number;
    l2ActivePowerRmsW?: number;
    l3ActivePowerRmsW?: number;
    currentOfferedA?: number;
    powerOfferedW?: number;
    stateOfCharge?: number;
    // Calculated values
    calcDeltaImportWh?: number;
    calcDeltaImportkWh?: number;
    calcDeltaExportkWh?: number;
    calcChargeRatekW?: number;
    calcOfferedChargeRatekW?: number;
    timestampDate: Date;
    timestampMs: number;
}

export interface IDeviceOwnerViewModel {
    deviceOwnerId?: number; // CAN be null when creating new one
    accountStatus?: DeviceOwnerDtoAccountStatusEnum;
    type?: DeviceOwnerDtoTypeEnum;
    name?: string;
    address?: string;
    countryCode?: string;
    mobileNo?: string;
    email?: string;
    ownerUserAccountId?: number;
    ownerUserAccount?: UserAccountSummary;
    ownerAddress?: AddressDto;
    internalNotes?: string;
}

export const phoneCountryCodeOptions: string[] = [
    "+61 (AUS 🇦🇺)",
    "+64 (NZ 🇳🇿)",
    "+60 (MY 🇲🇾)",
    "Rest of world 🌎",
];

export const getValidationHelpTextByCountryCodeOption = (
    countryCodeResponse: string | undefined,
) => {
    //hint to the user that AU/NZ selection should start with 0, international with +
    let prefix = "Must enter a valid mobile phone number";
    if (countryCodeResponse?.startsWith("+61"))
        prefix += " eg 0455 555 555"; //AU Mobile
    else if (countryCodeResponse?.startsWith("+64"))
        prefix += " eg 0255 555 555"; //NZ Mobile
    else if (!countryCodeResponse?.startsWith("+"))
        prefix += " eg +60169999999"; // rest of world
    return prefix;
};
export const getPhoneHintPrefixByCountryCodeOption = (
    countryCodeResponse: string | undefined,
): string | undefined => {
    // If countryCodeResponse is undefined, return undefined
    if (!countryCodeResponse) {
        return undefined;
    }

    //hint to the user that AU/NZ selection should start with 0
    let prefix: string;
    if (countryCodeResponse.startsWith("+61"))
        prefix = "04"; //AU Mobile
    else if (countryCodeResponse.startsWith("+64"))
        prefix = "02"; //NZ Mobile
    else if (countryCodeResponse.startsWith("+"))
        prefix = "0"; // Another dropdown country
    else prefix = "+"; // rest of world
    return prefix;
};
export const convertDeviceOwnerDtoToViewModel = (
    deviceOwnerDto: DeviceOwnerDto,
): IDeviceOwnerViewModel => {
    const { countryCode, localNumber } = convertNumberToCountryCodeAndNumber(
        deviceOwnerDto.mobileNo,
        phoneCountryCodeOptions,
    );

    return {
        deviceOwnerId: deviceOwnerDto.deviceOwnerId,
        accountStatus: deviceOwnerDto.accountStatus,
        type: deviceOwnerDto.type,
        name: deviceOwnerDto.name,
        address: deviceOwnerDto.address,
        countryCode: countryCode,
        mobileNo: localNumber,
        email: deviceOwnerDto.email,
        ownerUserAccountId: deviceOwnerDto.ownerUserAccountId,
        ownerUserAccount: deviceOwnerDto.ownerUserAccount,
        ownerAddress: deviceOwnerDto.ownerAddress,
        internalNotes: deviceOwnerDto.internalNotes,
    };
};

export const convertNumberToCountryCodeAndNumber = (
    mobileNo: string | undefined,
    phoneCountryCodeOptions: string[],
): { countryCode: string | undefined; localNumber: string | undefined } => {
    let countryCode: string | undefined;
    let localNumber: string | undefined;
    const countryCodeLookup = mobileNo?.substring(0, 3);
    if (countryCodeLookup) {
        countryCode = phoneCountryCodeOptions.find(
            (no) => no.substring(0, 3) === countryCodeLookup,
        );
        if (countryCode) {
            // Found +61 or +64:
            const trimmedNumber = mobileNo?.substring(3);
            localNumber = trimmedNumber?.startsWith("0")
                ? trimmedNumber
                : `0${trimmedNumber}`;
        } else {
            // Rest of world. Use userAccountDto.mobileNo directly. Keep + in localNumber
            countryCode =
                phoneCountryCodeOptions[phoneCountryCodeOptions.length - 1];
            localNumber = mobileNo;
        }
    }

    return { countryCode, localNumber };
};

export const convertAreaCodeAndPhoneToNumber = (
    countryCodeResponse: string | undefined,
    localPhoneNo: string,
): string => {
    /* Post-validation */
    if (!localPhoneNo) return "";
    let retNo: string;
    if (countryCodeResponse?.startsWith("+")) {
        //selected +61 or +64
        const countryCode = countryCodeResponse?.split(" ")[0] || "";
        const phoneNo = localPhoneNo.startsWith("0")
            ? localPhoneNo.substring(1)
            : localPhoneNo;
        retNo = `${countryCode}${phoneNo}`.trim().replace(/\s/g, "");
    } else {
        // selected rest of world
        if (!localPhoneNo.startsWith("+")) retNo = `+${localPhoneNo}`;
        else retNo = localPhoneNo;
    }
    return retNo;
};

export const convertDeviceOwnerViewModelToDto = (
    deviceOwnerViewModel: IDeviceOwnerViewModel,
): DeviceOwnerDto => {
    return {
        deviceOwnerId: deviceOwnerViewModel.deviceOwnerId,
        accountStatus: deviceOwnerViewModel.accountStatus,
        type: deviceOwnerViewModel.type,
        name: deviceOwnerViewModel.name,
        address: deviceOwnerViewModel.address,
        mobileNo: convertAreaCodeAndPhoneToNumber(
            deviceOwnerViewModel.countryCode,
            deviceOwnerViewModel.mobileNo || "",
        ),
        email: deviceOwnerViewModel.email,
        ownerUserAccountId: deviceOwnerViewModel.ownerUserAccountId,
        ownerUserAccount: deviceOwnerViewModel.ownerUserAccount,
        ownerAddress: deviceOwnerViewModel.ownerAddress,
        internalNotes: deviceOwnerViewModel.internalNotes,
    };
};

/* Temporary For prototyping */
export interface IAddressSearchResponse {
    results: IMapResult[];
    summary: unknown;
}

interface IMapResult {
    address: IDetailedAddress;
    id: string;
    position: IResultPosition;
    score: number;
    type: string;
    viewport: unknown;
}

interface IResultPosition {
    lat: number;
    lon: number;
}

interface IDetailedAddress {
    country: string;
    countryCode: string;
    countryCodeISO3: string;
    countrySecondarySubdivision: string;
    countrySubdivision: string;
    freeformAddress: string;
    localName: string;
    municipality: string;
    postalCode: string;
    streetName: string;
    streetNumber: string;
}
